/*
pricing-key.js
Builds the in-process price cache key. The key must encode every input that can
change the upstream response, including which upstream endpoint answered it.
*/
import { localeToRegion } from "@/lib/utils";

export const PRICE_KEY_VERSION = "v1";

export const PRICE_BACKEND = {
  BATCH: "batch",
  LEGACY: "legacy",
};

/**
 * Mirrors the `if (currency)` branch in fetchRawPriceData. The two upstreams
 * return different response shapes, so they must never share a cache bucket.
 */
export function priceBackendFor(currency) {
  return currency ? PRICE_BACKEND.BATCH : PRICE_BACKEND.LEGACY;
}

export function regionFor(locale) {
  return localeToRegion[locale] || "NL";
}

function canonicalizeValue(value) {
  if (value === undefined) return "undefined";
  if (value === null) return "null";

  if (Array.isArray(value)) {
    return `[${value.map(canonicalizeValue).join(",")}]`;
  }

  if (typeof value === "object") {
    const entries = Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonicalizeValue(value[key])}`);
    return `{${entries.join(",")}}`;
  }

  const serialized = JSON.stringify(value);
  return serialized === undefined ? "undefined" : serialized;
}

/**
 * Deterministic serialization: object keys sorted at every depth, array order
 * preserved, `undefined` and `null` rendered as distinct tokens.
 */
export function canonicalizeConfiguration(configuration) {
  return canonicalizeValue(configuration);
}

// Segments are JSON-quoted, so a separator character inside a value cannot be
// confused with the separator between segments.
export function buildPriceCacheKey({
  backend,
  userId,
  sku,
  configuration,
  currency,
  locale,
}) {
  const resolvedBackend = backend || priceBackendFor(currency);
  const region =
    resolvedBackend === PRICE_BACKEND.BATCH ? regionFor(locale) : null;

  return [
    PRICE_KEY_VERSION,
    resolvedBackend,
    canonicalizeValue(userId || "anon"),
    canonicalizeValue(sku),
    canonicalizeConfiguration(configuration),
    canonicalizeValue(currency),
    canonicalizeValue(region),
  ].join("|");
}
